import { useEffect, useState } from "react";
import { api, API } from "@/lib/api";
import { toast } from "sonner";
import { Download, Plus, Trash2, Edit3 } from "lucide-react";
import ComplaintFormDialog from "@/components/admin/ComplaintFormDialog";
import ComplaintEditDialog from "@/components/admin/ComplaintEditDialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const STATUSES = ["Pending", "In Progress", "Resolved"];

export default function ComplaintsTab({ onChange }) {
  const [rows, setRows] = useState([]);
  const [filter, setFilter] = useState("all");
  const [loading, setLoading] = useState(false);
  const [addOpen, setAddOpen] = useState(false);
  const [editing, setEditing] = useState(null);

  const remove = async (id) => {
    if (!window.confirm("Delete this complaint? This can't be undone.")) return;
    try {
      await api.delete(`/complaints/${id}`);
      toast.success("Complaint deleted");
      load();
      onChange?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Delete failed");
    }
  };

  const load = async () => {
    setLoading(true);
    try {
      const params = filter !== "all" ? { status: filter } : {};
      const { data } = await api.get("/complaints", { params });
      setRows(data);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line
  }, [filter]);

  const updateStatus = async (id, status) => {
    try {
      await api.put(`/complaints/${id}`, { status });
      toast.success("Status updated");
      load();
      onChange?.();
    } catch {
      toast.error("Update failed");
    }
  };

  const updateRemarks = async (id, admin_remarks) => {
    try {
      await api.put(`/complaints/${id}`, { admin_remarks });
      toast.success("Remarks saved");
      load();
    } catch {
      toast.error("Update failed");
    }
  };

  const handleExport = async () => {
    try {
      const token = localStorage.getItem("pg_token");
      const res = await fetch(`${API}/export/complaints`, {
        headers: { Authorization: `Bearer ${token}` },
        credentials: "include",
      });
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "complaints.csv";
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error("Export failed");
    }
  };

  const statusBadge = (s) => {
    if (s === "Resolved") return "bg-[#00CC66] text-white";
    if (s === "In Progress") return "bg-[#FF9900] text-black";
    return "bg-[#FF4D4D] text-white";
  };

  return (
    <div data-testid="complaints-tab">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-6">
        <div className="flex flex-wrap gap-2">
          {["all", ...STATUSES].map((s) => (
            <button
              key={s}
              onClick={() => setFilter(s)}
              data-testid={`complaint-filter-${s.toLowerCase().replace(" ", "-")}`}
              className={`px-4 py-2 font-bold text-sm border-2 border-[#0A0A0A] rounded-full transition-transform ${
                filter === s
                  ? "bg-[#0A0A0A] text-[#FFE600] shadow-brutal-sm"
                  : "bg-white hover:shadow-brutal-sm"
              }`}
            >
              {s === "all" ? "All" : s}
            </button>
          ))}
        </div>
        <div className="flex gap-2">
          <button
            onClick={() => setAddOpen(true)}
            data-testid="complaints-add-button"
            className="inline-flex items-center gap-2 font-bold px-5 py-2.5 border-2 border-[#0A0A0A] rounded-full bg-[#FFE600] shadow-brutal hover:translate-x-[2px] hover:translate-y-[2px] hover:shadow-none transition-transform"
          >
            <Plus size={16} /> Add Complaint
          </button>
          <button
            onClick={handleExport}
            data-testid="complaints-export-button"
            className="inline-flex items-center gap-2 font-bold px-5 py-2.5 border-2 border-[#0A0A0A] rounded-full bg-white shadow-brutal-sm hover:translate-x-[2px] hover:translate-y-[2px] hover:shadow-none transition-transform"
          >
            <Download size={16} /> Export CSV
          </button>
        </div>
      </div>

      <div className="space-y-4">
        {loading && (
          <div className="brutal-card p-8 text-center font-medium text-[#4A4A4A]">Loading...</div>
        )}
        {!loading && rows.length === 0 && (
          <div className="brutal-card p-8 text-center font-medium text-[#4A4A4A]">
            No complaints yet.
          </div>
        )}
        {rows.map((c) => (
          <div key={c.id} className="brutal-card p-6 bg-white" data-testid={`complaint-${c.id}`}>
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div>
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-heading font-black text-xl">{c.student_name}</span>
                  <span className="text-xs font-bold uppercase tracking-widest px-2 py-0.5 border-2 border-[#0A0A0A] rounded-full bg-[#00E5FF]">
                    Flat {c.flat_number}
                  </span>
                  <span className="text-xs font-bold uppercase tracking-widest px-2 py-0.5 border-2 border-[#0A0A0A] rounded-full bg-[#FFE600]">
                    {c.category}
                  </span>
                </div>
                <div className="text-xs font-medium text-[#4A4A4A] mt-1">
                  {c.date_of_complaint}
                </div>
              </div>
              <div className="flex items-center gap-2">
                <span
                  className={`px-3 py-1 border-2 border-[#0A0A0A] rounded-full text-xs font-bold ${statusBadge(
                    c.status
                  )}`}
                  data-testid={`complaint-status-badge-${c.id}`}
                >
                  {c.status}
                </span>
                <button
                  onClick={() => setEditing(c)}
                  data-testid={`complaint-edit-${c.id}`}
                  className="p-2 border-2 border-[#0A0A0A] rounded-full bg-[#FFE600] hover:shadow-brutal-sm transition-shadow"
                  title="Edit complaint"
                >
                  <Edit3 size={14} strokeWidth={2.5} />
                </button>
                <button
                  onClick={() => remove(c.id)}
                  data-testid={`complaint-delete-${c.id}`}
                  className="p-2 border-2 border-[#0A0A0A] rounded-full bg-[#FF4D4D] text-white hover:shadow-brutal-sm transition-shadow"
                  title="Delete complaint"
                >
                  <Trash2 size={14} strokeWidth={2.5} />
                </button>
              </div>
            </div>
            <p className="mt-4 font-medium">{c.description}</p>
            <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <div className="text-xs font-bold uppercase tracking-widest mb-1.5">
                  Update status
                </div>
                <Select
                  value={c.status}
                  onValueChange={(v) => updateStatus(c.id, v)}
                >
                  <SelectTrigger
                    data-testid={`complaint-status-select-${c.id}`}
                    className="border-2 border-[#0A0A0A] rounded-xl bg-white font-bold"
                  >
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="border-2 border-[#0A0A0A]">
                    {STATUSES.map((s) => (
                      <SelectItem key={s} value={s}>
                        {s}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <div className="text-xs font-bold uppercase tracking-widest mb-1.5">
                  Admin remarks
                </div>
                <RemarksEditor
                  initial={c.admin_remarks || ""}
                  onSave={(v) => updateRemarks(c.id, v)}
                  testId={`complaint-remarks-${c.id}`}
                />
              </div>
            </div>
          </div>
        ))}
      </div>

      <ComplaintFormDialog
        open={addOpen}
        onOpenChange={setAddOpen}
        onSaved={() => {
          load();
          onChange?.();
        }}
      />
      <ComplaintEditDialog
        open={!!editing}
        onOpenChange={(o) => !o && setEditing(null)}
        complaint={editing}
        onSaved={() => {
          load();
          onChange?.();
        }}
      />
    </div>
  );
}

function RemarksEditor({ initial, onSave, testId }) {
  const [val, setVal] = useState(initial);
  useEffect(() => setVal(initial), [initial]);
  return (
    <div className="flex gap-2">
      <input
        data-testid={testId}
        value={val}
        onChange={(e) => setVal(e.target.value)}
        placeholder="Add response..."
        className="flex-1 px-3 py-2 border-2 border-[#0A0A0A] rounded-xl bg-white font-medium focus:outline-none focus:ring-2 focus:ring-[#0A0A0A] focus:ring-offset-2"
      />
      <button
        onClick={() => onSave(val)}
        data-testid={`${testId}-save`}
        className="px-4 py-2 font-bold border-2 border-[#0A0A0A] rounded-full bg-[#FFE600] hover:shadow-brutal-sm transition-shadow"
      >
        Save
      </button>
    </div>
  );
}
